import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import type { SuggestionItem, SuggestionType } from "@/types/suggestions";
import { type PersonalSuggestionCreate, type PersonalSuggestionUpdate, createPersonalSuggestion, createSuggestion, deletePersonalSuggestion, deleteSuggestion, fetchPersonalSuggestions, fetchSuggestions, reorderSuggestions, updatePersonalSuggestion, updateSuggestion } from "@/api/provider/suggestions";
import { suggestionQueryKeys } from "@/query/keys/suggestions";
export function useSuggestions(suggestionTypes, patientId, eventId, scope) {
  const $ = _c(13);
  let t0;
  if ($[0] !== eventId || $[1] !== patientId || $[2] !== scope || $[3] !== suggestionTypes) {
    t0 = suggestionQueryKeys.list(suggestionTypes, patientId, eventId, scope);
    $[0] = eventId;
    $[1] = patientId;
    $[2] = scope;
    $[3] = suggestionTypes;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== eventId || $[6] !== patientId || $[7] !== scope || $[8] !== suggestionTypes) {
    t1 = () => fetchSuggestions(suggestionTypes, patientId, eventId, scope);
    $[5] = eventId;
    $[6] = patientId;
    $[7] = scope;
    $[8] = suggestionTypes;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t0 || $[11] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      select: _temp
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return useQuery(t2);
}

// Personal suggestions appear in the management list (`personal`) and, on opt-in
// surfaces, in the shared @-mention fetches (`all`). Optimistic mutations patch
// both cache families and snapshot them for rollback.
function _temp(data) {
  return data ?? [];
}
const PERSONAL_CACHE_FILTERS = [{
  queryKey: suggestionQueryKeys.personal
}, {
  queryKey: suggestionQueryKeys.all
}];
function patchPersonalCaches(updater: (old: SuggestionItem[] | undefined) => SuggestionItem[] | undefined) {
  for (const filter of PERSONAL_CACHE_FILTERS) {
    queryClient.setQueriesData<SuggestionItem[]>(filter, updater);
  }
}
function snapshotPersonalCaches() {
  return PERSONAL_CACHE_FILTERS.flatMap(filter => queryClient.getQueriesData<SuggestionItem[]>(filter));
}
function restorePersonalCaches(previous: ReturnType<typeof snapshotPersonalCaches>) {
  previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
}
export function usePersonalSuggestions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: suggestionQueryKeys.personal,
      queryFn: _temp2,
      select: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
function _temp3(data) {
  return data ?? [];
}
function _temp2() {
  return fetchPersonalSuggestions();
}
export function useCreatePersonalSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp5(created) {
  if (!created) {
    return;
  }
  patchPersonalCaches(old => old ? [...old, created] : old);
}
function _temp4(suggestion) {
  return createPersonalSuggestion(suggestion);
}
export function useUpdatePersonalSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp6,
      onMutate: _temp7,
      onError: _temp8
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp8(_err, _vars, context) {
  if (context?.previous) {
    restorePersonalCaches(context.previous);
  }
}
async function _temp7(updated) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.personal
  });
  const previous = snapshotPersonalCaches();
  patchPersonalCaches(old => old?.map(s => s.id === updated.id ? {
    ...s,
    ...updated
  } : s));
  return {
    previous
  };
}
function _temp6(suggestion) {
  return updatePersonalSuggestion(suggestion);
}
export function useDeletePersonalSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp9,
      onMutate: _temp10,
      onError: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp11(_err, _vars, context) {
  if (context?.previous) {
    restorePersonalCaches(context.previous);
  }
}
async function _temp10(suggestionId_0) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.personal
  });
  const previous = snapshotPersonalCaches();
  patchPersonalCaches(old => old?.filter(s => s.id !== suggestionId_0));
  return {
    previous
  };
}
function _temp9(suggestionId) {
  return deletePersonalSuggestion(suggestionId);
}
export function useCreateSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp12,
      onMutate: _temp13,
      onError: _temp15,
      onSettled: _temp16
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp16() {
  queryClient.invalidateQueries({
    queryKey: suggestionQueryKeys.all
  });
}
function _temp15(_err, _vars, context) {
  context?.previous.forEach(_temp14);
}
function _temp14(t0) {
  const [key, data] = t0;
  return queryClient.setQueryData(key, data);
}
async function _temp13(newSuggestion) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.all
  });
  const previous = queryClient.getQueriesData({
    queryKey: suggestionQueryKeys.all
  });
  queryClient.setQueriesData({
    queryKey: suggestionQueryKeys.all
  }, old => old ? [...old, newSuggestion] : [newSuggestion]);
  return {
    previous
  };
}
function _temp12(suggestion) {
  return createSuggestion(suggestion);
}
export function useUpdateSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp17,
      onMutate: _temp18,
      onError: _temp20,
      onSettled: _temp21
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp21() {
  queryClient.invalidateQueries({
    queryKey: suggestionQueryKeys.all
  });
}
function _temp20(_err, _vars, context) {
  context?.previous.forEach(_temp19);
}
function _temp19(t0) {
  const [key, data] = t0;
  return queryClient.setQueryData(key, data);
}
async function _temp18(updated) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.all
  });
  const previous = queryClient.getQueriesData({
    queryKey: suggestionQueryKeys.all
  });
  queryClient.setQueriesData({
    queryKey: suggestionQueryKeys.all
  }, old => old?.map(s => s.id === updated.id ? {
    ...s,
    ...updated
  } : s));
  return {
    previous
  };
}
function _temp17(suggestion) {
  return updateSuggestion(suggestion);
}
export function useDeleteSuggestion() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp22,
      onMutate: _temp23,
      onError: _temp25,
      onSettled: _temp26
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp26() {
  queryClient.invalidateQueries({
    queryKey: suggestionQueryKeys.all
  });
}
function _temp25(_err, _vars, context) {
  context?.previous.forEach(_temp24);
}
function _temp24(t0) {
  const [key, data] = t0;
  return queryClient.setQueryData(key, data);
}
async function _temp23(deleted) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.all
  });
  const previous = queryClient.getQueriesData({
    queryKey: suggestionQueryKeys.all
  });
  queryClient.setQueriesData({
    queryKey: suggestionQueryKeys.all
  }, old => old?.filter(s => s.id !== deleted.id));
  return {
    previous
  };
}
function _temp22(suggestion) {
  return deleteSuggestion(suggestion);
}
export function applySavedOrder(list: SuggestionItem[], ids: string[]): SuggestionItem[] {
  const savedById = new Map(list.filter(s => s.type === "SAVED").map(s => [s.id, s]));
  const reordered = ids.flatMap(id => savedById.get(id) ?? []);
  const reorderedIds = new Set(reordered.map(s => s.id));
  let next = 0;
  return list.map(s => s.type === "SAVED" && reorderedIds.has(s.id) ? reordered[next++] : s);
}
const REORDER_SUGGESTIONS_KEY = ["reorder-suggestions"];
export function useReorderSuggestions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: REORDER_SUGGESTIONS_KEY,
      scope: {
        id: "reorder-suggestions"
      },
      mutationFn: _temp27,
      onMutate: _temp28,
      onSuccess: _temp29,
      onError: _temp31
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp31(_err, _vars, context) {
  if (queryClient.isMutating({
    mutationKey: REORDER_SUGGESTIONS_KEY
  }) > 1) {
    return;
  }
  context?.previous.forEach(_temp30);
  queryClient.invalidateQueries({
    queryKey: suggestionQueryKeys.all
  });
}
function _temp30(t0) {
  const [key, data] = t0;
  return queryClient.setQueryData(key, data);
}
function _temp29() {
  queryClient.invalidateQueries({
    queryKey: suggestionQueryKeys.all,
    refetchType: "none"
  });
}
async function _temp28(ids_0) {
  await queryClient.cancelQueries({
    queryKey: suggestionQueryKeys.all
  });
  const previous = queryClient.getQueriesData({
    queryKey: suggestionQueryKeys.all
  });
  queryClient.setQueriesData({
    queryKey: suggestionQueryKeys.all
  }, old => old && applySavedOrder(old, ids_0));
  return {
    previous
  };
}
function _temp27(ids) {
  return reorderSuggestions(ids);
}